import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UserDomainRole } from '@/types/User';

export type ActiveStatusFilter = 'all' | 'active' | 'deactivated';
export type SortDirection = 'asc' | 'desc';

export type AdminUsersPageState = {
  filters: {
    nameOrEmailSearch: string;
    role: UserDomainRole | null;
    teamNameContains: string;
    activeStatus: ActiveStatusFilter;
  };
  sort: {
    field: 'displayName';
    direction: SortDirection;
  };
  createBreakGlassModalOpen: boolean;
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const initialState: AdminUsersPageState = {
  filters: {
    nameOrEmailSearch: '',
    role: null,
    teamNameContains: '',
    activeStatus: 'all',
  },
  sort: {
    field: 'displayName',
    direction: 'asc',
  },
  createBreakGlassModalOpen: false,
  submitInProgress: false,
  lastErrorKey: null,
};

export const adminUsersPageSlice = createSlice({
  name: 'adminUsersPage',
  initialState,
  reducers: {
    setNameOrEmailSearch(state, action: PayloadAction<string>) {
      state.filters.nameOrEmailSearch = action.payload;
    },
    setRoleFilter(state, action: PayloadAction<UserDomainRole | null>) {
      state.filters.role = action.payload;
    },
    setTeamNameContains(state, action: PayloadAction<string>) {
      state.filters.teamNameContains = action.payload;
    },
    setActiveStatus(state, action: PayloadAction<ActiveStatusFilter>) {
      state.filters.activeStatus = action.payload;
    },
    openCreateBreakGlassModal(state) {
      state.createBreakGlassModalOpen = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeCreateBreakGlassModal(state) {
      state.createBreakGlassModalOpen = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
  },
});

export const {
  setNameOrEmailSearch,
  setRoleFilter,
  setTeamNameContains,
  setActiveStatus,
  openCreateBreakGlassModal,
  closeCreateBreakGlassModal,
  setSubmitInProgress,
  setLastErrorKey,
} = adminUsersPageSlice.actions;

type AppRootState = {
  adminUsersPage: AdminUsersPageState;
};

export const selectAdminUsersPage = (state: AppRootState) =>
  state.adminUsersPage;
